/**
 * Routes. The office app is behind a session; the sign-in and set-password
 * pages are not.
 */
import { type ReactElement, useEffect, useState } from 'react'
import {
  BrowserRouter, Navigate, Route, Routes, useLocation, useParams,
} from 'react-router-dom'
import type { MeResponse } from '@rowted/shared'
import { CrewApp } from './pages/crew-app.js'
import { CrewDoor, NoCompanyHere } from './pages/address.js'
import { Gallery } from './dev/gallery.js'
import { PortalEnter, PortalHome, PortalProperty } from './pages/portal.js'
import { PortalQuote } from './pages/portal-quote.js'
import { QuotePreview } from './pages/quote-preview.js'
import { AdminClients } from './pages/admin/clients.js'
import { AdminClient } from './pages/admin/client.js'
import { AdminProfile } from './pages/admin/profile.js'
import { AdminAccountPage } from './pages/admin/account.js'
import { AdminIntegrations } from './pages/admin/integrations.js'
import { AdminSettings } from './pages/admin/settings.js'
import { AdminHealth } from './pages/admin/health.js'
import { AdminAudit } from './pages/admin/audit.js'
import { AdminAdmins } from './pages/admin/admins.js'
import { AcceptInvite } from './pages/admin/accept-invite.js'
import { MyHours } from './pages/my-hours.js'
import { CrewSheetPage } from './visit/crew-sheet.js'
import { Office } from './pages/office.js'
import { CrewCloseout } from './pages/crew-closeout.js'
import { CrewQuickCheck } from './pages/crew-quick-check.js'
import { SignIn } from './pages/sign-in.js'
import { SetPassword } from './pages/set-password.js'
import { DemoSignup, Signup } from './pages/signup.js'
import { SetUp, remainingSteps } from './pages/set-up.js'
import { applyTheme, readTheme } from './lib/theme.js'
import { useHost } from './lib/use-host.js'
import { useMe } from './lib/use-me.js'

export function App() {
  // The theme applies before the first paint of any route, so a dark-mode
  // reload never flashes light.
  const [themed, setThemed] = useState(false)
  useEffect(() => {
    applyTheme(readTheme())
    setThemed(true)
  }, [])

  /*
   * Whose address this is, before any route draws — `docs/identity-and-sign-in.md`
   * §5.2. A subdomain that names no company gets one page and no router: every
   * route below assumes it is either the front door or *a* company, and a
   * router that answered `/jobs` at an address belonging to nobody would ask
   * for a session it can never be given.
   */
  const host = useHost()

  if (!themed || host.state === 'loading') return null
  if (host.state === 'nobody') return <NoCompanyHere />

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/sign-in" element={<SignIn />} />
        {/*
          * Landing after a switch (`identity-and-sign-in.md` §2.2). The same
          * component: the server answers `/auth/switch/arrive` with the same
          * shape it answers a sign-in, so the code step and its panels are
          * already drawn. Without this route `/switch?t=…` fell through to
          * the catch-all and every switch ended at a sign-in page.
          */}
        <Route path="/switch" element={<SignIn />} />
        {/* The front door for a new company (§6.13). Public, obviously. */}
        <Route path="/signup" element={<Signup />} />
        {/* §6.13: a fresh clone of the demo world, per visitor. */}
        <Route path="/demo" element={<DemoSignup />} />
        <Route path="/set-password" element={<SetPassword />} />
        {/*
          * The customer portal (§6.11). Its own surface, like the crew app:
          * no left nav, no scope selector, no session of ours —
          * a portal cookie, and a frame that reads like a text message.
          */}
        <Route path="/portal/enter/:token" element={<PortalEnter />} />
        <Route path="/portal" element={<PortalHome />} />
        <Route path="/portal/p/:id" element={<PortalProperty />} />
        <Route path="/portal/quote/:id" element={<PortalQuote />} />
        {/*
          * Rowted Admin — `docs/rowted-admin-v2.md` §4.2.
          *
          * The app's own frame with a black ADMIN tag, not a surface of its
          * own: §0.1 says one frame, one word different. Its session is a
          * platform cookie, so `RequireSession` — which reads the tenant one —
          * is deliberately not here; each page asks `/platform/me` for itself
          * and sends a stranger to `/sign-in?next=…`.
          */}
        <Route path="/admin" element={<Navigate to="/admin/clients" replace />} />
        <Route path="/admin/clients" element={<AdminClients />} />
        <Route path="/admin/clients/:id" element={<AdminClient />} />
        <Route path="/admin/integrations" element={<AdminIntegrations />} />
        {/* Before `/admin/admins/:id`, which it is not: this is *your* profile
            — password, two-step, devices — and that is the record another
            owner opens to change your role. Aaron's item 4. */}
        <Route path="/admin/account" element={<AdminAccountPage />} />
        <Route path="/admin/admins" element={<AdminAdmins />} />
        <Route path="/admin/admins/:id" element={<AdminProfile />} />
        <Route path="/admin/settings" element={<AdminSettings />} />
        <Route path="/admin/health" element={<AdminHealth />} />
        <Route path="/admin/audit" element={<AdminAudit />} />
        <Route path="/admin/accept-invite/:token" element={<AcceptInvite />} />

        {/*
          * The console's addresses, kept for a release. Somebody has
          * `/console/settings` bookmarked and a 404 is a worse answer than a
          * redirect — §0.1 says "keep `/console*` as redirects".
          */}
        <Route path="/console/sign-in" element={<Navigate to="/sign-in" replace />} />
        <Route path="/admin/sign-in" element={<Navigate to="/sign-in" replace />} />
        <Route path="/console" element={<Navigate to="/admin/clients" replace />} />
        <Route path="/console/clients/:id" element={<ConsoleClientRedirect />} />

        <Route path="/console/settings" element={<Navigate to="/admin/settings" replace />} />
        <Route path="/console/health" element={<Navigate to="/admin/health" replace />} />
        {/* The gallery is a design reference, so it renders without a session. */}
        <Route path="/dev/gallery" element={<Gallery />} />
        {/* Plan §3.7: the crew app has its own frame — no left nav, no scope
            selector — so it hangs off the router beside the office app rather
            than inside it. */}
        {/*
          * §5.4: the crew app is a company's own address, because a shared iPad
          * belongs to one company and its PIN screen has to say which. On the
          * front door there is no company to name, so the route asks for the
          * address instead of asking for a session it cannot scope.
          */}
        <Route
          path="/crew"
          element={host.host.tenant
            ? <RequireSession render={(me) => <CrewApp me={me} />} />
            : <CrewDoor />}
        />
        {/* My hours is a phone screen (§6.6) and belongs to the person, not to
            the office app — an office user opens the same URL. */}
        <Route path="/my-hours" element={<RequireSession render={(me) => <MyHours me={me} />} />} />
        {/* ⋯ Print the crew sheet (`docs/visit-page.md` §7.2): a print-ready
            page in its own tab, without the office frame around it. */}
        <Route path="/visits/:id/crew-sheet" element={<RequireSession render={() => <CrewSheetPage />} />} />
        {/* §6.8: the morning check happens at the truck, before the day
            starts, so it wears the crew frame rather than the office one. */}
        <Route
          path="/crew/check/:equipmentId"
          element={<RequireSession render={(me) => <CrewQuickCheck me={me} />} />}
        />
        {/* The close-out is the crew app's too (§3.7). */}
        <Route
          path="/crew/closeout/:visitId"
          element={<RequireSession render={(me) => <CrewCloseout me={me} />} />}
        />
        {/* Where the invite link hands over once the password is set. No skip:
            an account mid-invite is not finished yet. */}
        <Route
          path="/set-up"
          element={
            <RequireSession
              gate={false}
              render={(me) => (
                <SetUp me={me} afterPassword onDone={() => window.location.assign('/')} />
              )}
            />
          }
        />
        {/*
          * The proposal as the customer sees it (`docs/proposal.md` §10.2).
          *
          * Above the catch-all and outside `Landing` on purpose: the page is
          * worth opening precisely because it has none of our chrome around
          * it. It still needs an office session — a draft is not something a
          * customer may read.
          */}
        <Route
          path="/quotes/:id/preview"
          element={<RequireSession render={() => <QuotePreview />} />}
        />
        {/* The office app routes internally, so everything else below the
            session check is one entry here. */}
        <Route path="/*" element={<RequireSession render={(me) => <Landing me={me} />} />} />
      </Routes>
    </BrowserRouter>
  )
}

/*
 * Skipping is remembered per tab, so a prompt somebody has already answered
 * does not reappear on every navigation. Deliberately not persistent: a new
 * tab tomorrow asks again, which is the whole point of a prompt.
 */
const SKIP_KEY = 'rowted.set-up-skipped'

function skipped(): boolean {
  try {
    return window.sessionStorage.getItem(SKIP_KEY) === 'yes'
  } catch {
    return false
  }
}

function RequireSession({
  render, gate = true,
}: {
  render: (me: MeResponse) => ReactElement
  /**
   * False on `/set-up` itself, which is the flow — gating it would gate it
   * behind itself.
   */
  gate?: boolean
}) {
  const state = useMe()
  // Cleared once they finish, so the flow does not reappear on the next
  // render before `/me` has been read again.
  const [setUp, setSetUp] = useState(false)

  if (state.status === 'loading') {
    return (
      <div className="flex min-h-app items-center justify-center bg-bg">
        <span className="text-cell text-text-muted">Loading…</span>
      </div>
    )
  }

  if (state.status === 'signed-out') return <Navigate to="/sign-in" replace />

  if (state.status === 'error') {
    return (
      <div className="flex min-h-app items-center justify-center bg-bg p-4">
        <div className="max-w-[420px] rounded-card border border-line bg-surface p-6">
          <h1 className="m-0 text-title">Something went wrong</h1>
          <p className="mb-0 mt-2 text-cell text-text-muted">{state.message}</p>
        </div>
      </div>
    )
  }

  /*
   * §6.10's invite → password → MFA → PIN. An account that still owes a second
   * factor or a PIN is prompted before the app opens: a crew member who lands
   * in the crew app without a PIN can see the whole day and never clock in,
   * and nothing on that screen tells them why.
   *
   * It is a prompt, not a wall. Making it a wall meant one deploy could lock
   * an existing account out of a product it had been using all week — and an
   * Owner has nobody above them to reset it. The invite hand-off at `/set-up`
   * is the one place with no way past, because there the account is not
   * finished yet.
   */
  if (gate && !setUp && !skipped() && remainingSteps(state.me).length > 0) {
    return (
      <SetUp
        me={state.me}
        onDone={() => { setSetUp(true); window.location.reload() }}
        onSkip={() => {
          try { window.sessionStorage.setItem(SKIP_KEY, 'yes') } catch { /* private window */ }
          setSetUp(true)
        }}
      />
    )
  }

  return render(state.me)
}

/**
 * Where a session lands. Plan §8: the role carries the app someone works in,
 * so a crew lead who opens the bare URL gets the crew app and everybody else
 * gets the office app, whose home is Activity — without having to know the
 * path. The crew app is the only special landing: everyone who isn't crew has
 * Activity (Aaron, Oct 23).
 */
const APP_HOME: Partial<Record<MeResponse['defaultApp'], string>> = {
  crew_app: '/crew',
}

function Landing({ me }: { me: MeResponse }) {
  const location = useLocation()

  /*
   * Only the bare URL redirects. Somebody who typed `/jobs` asked for the
   * office app, and a crew lead is allowed there — the permission matrix
   * decides what they see once they arrive, not this.
   */
  const home = APP_HOME[me.defaultApp]
  if (home && location.pathname === '/') return <Navigate to={home} replace />

  return <Office me={me} />
}

/**
 * `/console/clients/:id` → `/admin/clients/:id`, keeping the id.
 *
 * A component rather than a `<Navigate>` because the target depends on the
 * path: `Navigate` takes a string, and the string has to be built from the
 * param this route matched.
 */
function ConsoleClientRedirect() {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={id ? `/admin/clients/${id}` : '/admin/clients'} replace />
}
